import { z } from 'zod';
import { TenantStatus } from '@yourstore/domain';

/**
 * The operator wire, as this console understands it.
 *
 * It mirrors `apps/admin-api/src/wire.ts` by hand and on purpose. Apps do not
 * cross-import (dev.md §3: the dependency arrows run through `packages/`, and
 * that module's types come from `@yourstore/store`, which would drag Drizzle
 * and the `postgres` driver into a browser bundle). What the two files *do*
 * share is `@yourstore/domain` — `TenantStatus` is one enum rather than two
 * lookalikes, which matters because the whole lifecycle screen is built from
 * it and `authority: "live"` hangs off one of its members.
 *
 * Everything the console renders is parsed through these schemas first. On the
 * staff surface that rule exists because a malformed row would be written into
 * a mirror and repeated offline for as long as the tunnel lasts; here there is
 * no mirror, and the reason is different: this surface's screens are the
 * evidence an operator acts on — which keys are live, which tenant is `live`,
 * which deliveries failed — and a field that silently arrived as `undefined`
 * would render as an empty cell that reads like a fact.
 *
 * **What is deliberately absent is the point of §6.3.** There is no client, no
 * appointment, no settlement and no webhook *payload* in this file, because
 * there is none on the wire: `admin_api` holds no grant on those tables
 * (migration 0008) and the console must not even ask. If a schema here ever
 * grows a guest-shaped field, that is the bug.
 */

/* ------------------------------------------------------------------ */
/* The shared envelope                                                 */
/* ------------------------------------------------------------------ */

export const ErrorBody = z.object({
  error: z.object({
    code: z.enum([
      'invalid',
      'unauthenticated',
      'forbidden',
      'not_found',
      'conflict',
      'rate_limited',
      'unavailable',
      'unsupported',
    ]),
    message: z.string(),
    detail: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ErrorBody = z.infer<typeof ErrorBody>;

export const ModuleStatus = z.enum(['on', 'off', 'trial']);
export type ModuleStatus = z.infer<typeof ModuleStatus>;

export const KeyEnv = z.enum(['test', 'live']);
export type KeyEnv = z.infer<typeof KeyEnv>;

export const DeliveryStatus = z.enum(['queued', 'delivering', 'delivered', 'failed', 'exhausted']);
export type DeliveryStatus = z.infer<typeof DeliveryStatus>;

/* ------------------------------------------------------------------ */
/* The fleet                                                           */
/* ------------------------------------------------------------------ */

export const FleetTenant = z.object({
  tenant_id: z.string(),
  name: z.string(),
  timezone: z.string(),
  currency: z.string(),
  status: TenantStatus,
  plan: z.string(),
  created_at: z.string(),
  modules: z.array(z.string()),
  module_count: z.number(),
  active_keys: z.number(),
  revoked_keys: z.number(),
  webhook_endpoints: z.number(),
  webhook_failing: z.boolean(),
  last_activity_at: z.string().nullable(),
});
export type FleetTenant = z.infer<typeof FleetTenant>;

export const FleetPage = z.object({
  tenants: z.array(FleetTenant),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type FleetPage = z.infer<typeof FleetPage>;

/* ------------------------------------------------------------------ */
/* One tenant                                                          */
/* ------------------------------------------------------------------ */

export const ModuleRow = z.object({
  module: z.string(),
  status: ModuleStatus,
  enabled_at: z.string().nullable(),
  label: z.string(),
  depends_on: z.array(z.string()),
});
export type ModuleRow = z.infer<typeof ModuleRow>;

/** Prefixes, scopes, status and last use. Never a hash, never a plaintext (K5). */
export const ApiKeyRow = z.object({
  key_id: z.string(),
  integration: z.string(),
  env: KeyEnv,
  prefix: z.string(),
  status: z.enum(['active', 'revoked']),
  active_slot: z.number().nullable(),
  scopes: z.array(z.string()),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
});
export type ApiKeyRow = z.infer<typeof ApiKeyRow>;

export const WebhookEndpoint = z.object({
  endpoint_id: z.string(),
  integration: z.string(),
  url: z.string(),
  /** The Secret Manager *reference*. The secret itself is never a column. */
  secret_ref: z.string(),
  signature_header: z.string(),
  event_types: z.array(z.string()),
  active: z.boolean(),
  failing: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type WebhookEndpoint = z.infer<typeof WebhookEndpoint>;

/**
 * A delivery attempt — and note what a delivery does **not** carry: its
 * payload. That is the one field on this whole surface that could hold a
 * guest's name and arrival time, and `admin-api` omits it by projection. The
 * console therefore cannot render it, which is the intended shape of the
 * ladder rather than an oversight to fix later.
 */
export const Delivery = z.object({
  delivery_id: z.string(),
  tenant_id: z.string(),
  event_id: z.string(),
  event_type: z.string(),
  occurred_at: z.string(),
  endpoint_id: z.string(),
  integration: z.string(),
  url: z.string(),
  status: DeliveryStatus,
  attempts: z.number(),
  next_attempt_at: z.string(),
  last_error: z.string().nullable(),
  last_status_code: z.number().nullable(),
  delivered_at: z.string().nullable(),
  updated_at: z.string(),
});
export type Delivery = z.infer<typeof Delivery>;

/* ------------------------------------------------------------------ */
/* Domains (spec §6.4.2)                                               */
/* ------------------------------------------------------------------ */

export const DomainStatus = z.enum(['pending_dns', 'issuing', 'live']);
export type DomainStatus = z.infer<typeof DomainStatus>;

export const DomainKind = z.enum(['default', 'custom']);
export type DomainKind = z.infer<typeof DomainKind>;

/**
 * A hostname a tenant is served on.
 *
 * `served` is the server's own derivation, not something this file computes:
 * `app.tenant_for_host` (migration 0010) resolves `live` and nothing else, so
 * "is this business's website up?" has one answer and it is not assembled twice.
 * The console renders it and does not second-guess it — the same discipline the
 * rest of this file follows about `authority`.
 */
export const TenantDomain = z.object({
  hostname: z.string(),
  tenant_id: z.string(),
  kind: DomainKind,
  status: DomainStatus,
  served: z.boolean(),
  verified_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type TenantDomain = z.infer<typeof TenantDomain>;

export const DomainList = z.object({
  tenant_id: z.string(),
  domains: z.array(TenantDomain),
  note: z.string(),
});
export type DomainList = z.infer<typeof DomainList>;

export const DomainChanged = TenantDomain.extend({
  created: z.boolean().optional(),
  previous_status: DomainStatus.optional(),
  meaning: z.string(),
  note: z.string(),
});
export type DomainChanged = z.infer<typeof DomainChanged>;

/* ------------------------------------------------------------------ */
/* Branding — the theme editor (spec §6.4.3)                           */
/* ------------------------------------------------------------------ */

/**
 * The **theme contract, served as data**.
 *
 * This is the one place the console deliberately does not mirror a shape by
 * hand. `admin-api` sends the token table, the three presets with their whole
 * token sets, the block registry with its slot shapes, the sanitizer's
 * allowlist and every limit — and the editor builds its controls from that.
 *
 * The reason is the same one that put `MODULES` in `@yourstore/domain`: the
 * authority is the site bundle (`apps/site/src/theme/contract.ts` and
 * `sections/registry.ts`), apps do not cross-import, and a third hand-written
 * copy in this file would be a third thing to keep in step with a versioned
 * interface. There is one duplicate — `apps/admin-api/src/branding/contract.ts`,
 * which says at length why it exists and where it should end up — and the
 * console reads it over the wire instead of making a second.
 *
 * So these schemas describe the *envelope*, not the vocabulary: they say "a
 * token has a name, a type and a group", never which tokens exist. A service
 * that learns a new token is a console that renders a new control on the next
 * page load, with no rebuild.
 */
export const TokenType = z.enum(['color', 'length', 'number', 'font', 'text', 'url']);
export type TokenType = z.infer<typeof TokenType>;

export const TokenGroup = z.enum(['color', 'type', 'shape', 'space', 'imagery']);
export type TokenGroup = z.infer<typeof TokenGroup>;

export const ContractToken = z.object({
  name: z.string(),
  type: TokenType,
  label: z.string(),
  group: TokenGroup,
  css_variable: z.string(),
  hint: z.string(),
});
export type ContractToken = z.infer<typeof ContractToken>;

export const ContractPreset = z.object({
  name: z.string(),
  blurb: z.string(),
  tokens: z.record(z.string(), z.string()),
});
export type ContractPreset = z.infer<typeof ContractPreset>;

export const SlotKind = z.enum([
  'text',
  'prose',
  'richtext',
  'enum',
  'flag',
  'rows',
  'images',
  'image_url',
]);
export type SlotKind = z.infer<typeof SlotKind>;

export const ContractSlot = z.object({
  name: z.string(),
  kind: SlotKind,
  label: z.string(),
  max: z.number(),
  fallback: z.string().optional(),
  options: z.array(z.string()).optional(),
  hint: z.string().optional(),
});
export type ContractSlot = z.infer<typeof ContractSlot>;

export const ContractSection = z.object({
  type: z.string(),
  label: z.string(),
  why: z.string(),
  slots: z.array(ContractSlot),
});
export type ContractSection = z.infer<typeof ContractSection>;

export const ThemeContract = z.object({
  version: z.number(),
  source: z.array(z.string()),
  source_note: z.string(),
  tokens: z.array(ContractToken),
  token_groups: z.array(z.object({ key: TokenGroup, label: z.string() })),
  presets: z.array(ContractPreset),
  sections: z.array(ContractSection),
  default_arrangement: z.array(z.string()),
  /** `booking`. The bundle mounts the flow regardless, so the editor pins it. */
  pinned_section: z.string(),
  pinned_note: z.string(),
  sanitizer: z.object({
    allowed_tags: z.array(z.string()),
    allowed_attributes: z.record(z.string(), z.array(z.string())),
    allowed_link_schemes: z.array(z.string()),
    note: z.string(),
  }),
  limits: z.object({
    document_bytes: z.number(),
    custom_css_bytes: z.number(),
    history: z.number(),
  }),
});
export type ThemeContract = z.infer<typeof ThemeContract>;

export const BrandingSection = z.object({
  type: z.string(),
  id: z.string().optional(),
  slots: z.record(z.string(), z.unknown()).default({}),
});
export type BrandingSection = z.infer<typeof BrandingSection>;

/** Every field optional: `{}` is what a tenant who has published nothing has. */
export const BrandingDocument = z.object({
  contract_version: z.number().optional(),
  preset: z.string().optional(),
  tokens: z.record(z.string(), z.string()).optional(),
  sections: z.array(BrandingSection).optional(),
  site_title: z.string().optional(),
  tagline: z.string().optional(),
  custom_css: z.string().optional(),
});
export type BrandingDocument = z.infer<typeof BrandingDocument>;

export const BrandingHistoryRow = z.object({
  version: z.number(),
  at: z.string(),
  by: z.string(),
  note: z.string(),
  preset: z.string(),
});
export type BrandingHistoryRow = z.infer<typeof BrandingHistoryRow>;

const BrandingState = z.object({
  tenant_id: z.string(),
  branding: BrandingDocument,
  /** 0 means "never published" — a legitimate state, not a missing row. */
  branding_version: z.number(),
  published_at: z.string().nullable(),
  published_by: z.string().nullable(),
  publish_note: z.string(),
  custom_css_contract_version: z.number().nullable(),
  history: z.array(BrandingHistoryRow),
});

/** One day of the tenant's real week, which the preview may honestly paint. */
export const PreviewDay = z.object({
  weekday: z.number(),
  closed: z.boolean(),
  windows: z.array(z.object({ start: z.string(), end: z.string() })),
});
export type PreviewDay = z.infer<typeof PreviewDay>;

export const BrandingRead = BrandingState.extend({
  tenant: z.object({ name: z.string(), status: z.string(), timezone: z.string() }),
  /**
   * What the preview pane is allowed to be honest about.
   *
   * `catalog` is **always null** and the type says so. The operator console
   * holds no grant on `items` (migration 0008; `no-data-browser.test.ts` asserts
   * the `42501`), so §6.4.3's "live preview against the tenant's real catalog"
   * cannot be served here without breaking §6.3's "never a data browser". The
   * name and the week are real; the services are placeholders and the note says
   * so out loud.
   */
  preview: z.object({
    name: z.string(),
    hours: z.array(PreviewDay).catch([]),
    catalog: z.null(),
    catalog_note: z.string(),
  }),
  contract: ThemeContract,
  note: z.string(),
});
export type BrandingRead = z.infer<typeof BrandingRead>;

/** What the sanitizer did to one content zone, per zone, in the server's words. */
export const ZoneReport = z.object({
  path: z.string(),
  changed: z.boolean(),
  removed: z.array(z.string()),
  before_bytes: z.number(),
  after_bytes: z.number(),
});
export type ZoneReport = z.infer<typeof ZoneReport>;

export const BrandingPublished = BrandingState.extend({
  zones: z.array(ZoneReport),
  warnings: z.array(z.string()),
  document_bytes: z.number(),
  effective: z.string(),
});
export type BrandingPublished = z.infer<typeof BrandingPublished>;

export const BrandingRolledBack = BrandingState.extend({
  restored_from: z.number(),
  effective: z.string(),
});
export type BrandingRolledBack = z.infer<typeof BrandingRolledBack>;

export const AuditEntry = z.object({
  audit_id: z.string(),
  /** Null for genuinely cross-tenant work — a fleet listing (spec §10.9). */
  tenant_id: z.string().nullable(),
  actor: z.string(),
  action: z.string(),
  target: z.string(),
  detail: z.record(z.string(), z.unknown()),
  at: z.string(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

export const TenantDetail = z.object({
  tenant: z.object({
    tenant_id: z.string(),
    name: z.string(),
    timezone: z.string(),
    currency: z.string(),
    status: TenantStatus,
    plan: z.string(),
    /** Derived, never configured: `live` only when the status is (spec §4.4). */
    authority: z.enum(['live', 'approximate']),
  }),
  config: z
    .object({
      catalog_version: z.number(),
      hours: z.unknown(),
      config: z.unknown(),
    })
    .nullable(),
  modules: z.array(ModuleRow),
  keys: z.array(ApiKeyRow),
  webhook_endpoints: z.array(WebhookEndpoint),
  recent_deliveries: z.array(Delivery),
  recent_audit: z.array(AuditEntry),
});
export type TenantDetail = z.infer<typeof TenantDetail>;

/* ------------------------------------------------------------------ */
/* The writes                                                          */
/* ------------------------------------------------------------------ */

export const TenantCreated = z.object({
  tenant_id: z.string(),
  name: z.string(),
  timezone: z.string(),
  currency: z.string(),
  status: TenantStatus,
  plan: z.string(),
  modules: z.array(z.string()),
  next: z.string(),
});
export type TenantCreated = z.infer<typeof TenantCreated>;

export const StatusChanged = z.object({
  tenant_id: z.string(),
  previous_status: TenantStatus,
  status: TenantStatus,
  authority: z.enum(['live', 'approximate']),
  /** The key cache is 15 s (spec §5.2). Stated, not rounded to "instant". */
  effective_within_seconds: z.number(),
});
export type StatusChanged = z.infer<typeof StatusChanged>;

export const ModulesChanged = z.object({
  tenant_id: z.string(),
  modules: z.array(ModuleRow),
  enabled: z.array(z.string()),
  note: z.string(),
});
export type ModulesChanged = z.infer<typeof ModulesChanged>;

export const KeyList = z.object({
  tenant_id: z.string(),
  keys: z.array(ApiKeyRow),
  note: z.string(),
});
export type KeyList = z.infer<typeof KeyList>;

/**
 * The one response in the system that carries a live credential.
 *
 * `plaintext` exists here and nowhere else — not in `KeyList`, not in the audit
 * row, not in a log line. The console holds it in component state, shows it in
 * a copy box, and drops it when the box is dismissed; there is no store to put
 * it in and there must not be one.
 */
export const KeyIssued = z.object({
  key_id: z.string(),
  tenant_id: z.string(),
  integration: z.string(),
  env: KeyEnv,
  prefix: z.string(),
  scopes: z.array(z.string()),
  active_slot: z.number().nullable(),
  plaintext: z.string(),
  plaintext_note: z.string(),
  warnings: z.array(z.string()),
});
export type KeyIssued = z.infer<typeof KeyIssued>;

export const KeyRevoked = z.object({
  key_id: z.string(),
  tenant_id: z.string(),
  integration: z.string(),
  env: KeyEnv,
  freed_slot: z.number().nullable(),
  status: z.literal('revoked'),
  effective_within_seconds: z.number(),
});
export type KeyRevoked = z.infer<typeof KeyRevoked>;

export const WebhookList = z.object({
  tenant_id: z.string(),
  endpoints: z.array(WebhookEndpoint),
  note: z.string(),
});
export type WebhookList = z.infer<typeof WebhookList>;

export const WebhookSet = WebhookEndpoint.extend({
  tenant_id: z.string(),
  created: z.boolean(),
  note: z.string(),
});
export type WebhookSet = z.infer<typeof WebhookSet>;

export const DeliveryPage = z.object({
  tenant_id: z.string(),
  deliveries: z.array(Delivery),
  limit: z.number(),
  offset: z.number(),
});
export type DeliveryPage = z.infer<typeof DeliveryPage>;

export const Requeued = z.object({
  tenant_id: z.string(),
  since: z.string(),
  requeued: z.number(),
  created: z.number(),
  endpoints_cleared: z.number(),
  note: z.string(),
});
export type Requeued = z.infer<typeof Requeued>;

export const AuditPage = z.object({
  entries: z.array(AuditEntry),
  limit: z.number(),
  offset: z.number(),
  note: z.string(),
});
export type AuditPage = z.infer<typeof AuditPage>;

/* ------------------------------------------------------------------ */
/* Jobs                                                                */
/* ------------------------------------------------------------------ */

export const JobExecution = z.object({
  execution_id: z.string(),
  name: z.string(),
  /** Derived by the server from the counters, not read from a status field. */
  state: z.enum(['running', 'succeeded', 'failed', 'unknown']),
  created_at: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  task_count: z.number().nullable(),
  succeeded_count: z.number().nullable(),
  failed_count: z.number().nullable(),
});
export type JobExecution = z.infer<typeof JobExecution>;

export const JobKind = z.enum(['conformance', 'import']);
export type JobKind = z.infer<typeof JobKind>;

export const JobStarted = z.object({
  job: JobKind,
  tenant_id: z.string(),
  operation: z.string(),
  execution: JobExecution.nullable(),
  /** Import only — the server states which kind of run it was (spec §9). */
  execute: z.boolean().optional(),
  dry_run: z.boolean().optional(),
  note: z.string(),
});
export type JobStarted = z.infer<typeof JobStarted>;

export const ExecutionList = z.object({
  job: JobKind,
  executions: z.array(JobExecution),
});
export type ExecutionList = z.infer<typeof ExecutionList>;

export const ExecutionDetail = z.object({
  job: JobKind,
  execution: JobExecution,
});
export type ExecutionDetail = z.infer<typeof ExecutionDetail>;
